import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { KudosClassification } from '../types';
import { useDeleteKudos, useHideKudos, useNonKudosMessages, useReviewKudos, useUnreviewedKudos, useUsers, useIsAdmin } from '../hooks';
import { PageHeader } from '../components/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ChevronLeft, ChevronRight, EyeOff, Trash2 } from 'lucide-react';
import { KudosCard } from '../components/kudos';
import type { Kudos } from '../services/slackService';

interface KudosDisplayItem {
  id: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  message: string;
  createdAt: string;
  classification?: Kudos['classification'];
  classificationConfidence?: number;
  classificationReasoning?: string;
  isReviewed?: boolean;
  isHidden?: boolean;
}

const ITEMS_PER_PAGE = 20;

const SlackKudosReview: React.FC = () => {
  useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [unreviewedPage, setUnreviewedPage] = useState(1);
  const [nonKudosPage, setNonKudosPage] = useState(1);
  const unreviewed = useUnreviewedKudos(unreviewedPage, ITEMS_PER_PAGE);
  const nonKudos = useNonKudosMessages(nonKudosPage, ITEMS_PER_PAGE);
  const { data: users = [] } = useUsers(true);

  const reviewKudos = useReviewKudos();
  const hideKudos = useHideKudos();
  const deleteKudos = useDeleteKudos();

  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, adminLoading, navigate]);

  const mapKudosForDisplay = (items: Kudos[]): KudosDisplayItem[] => {
    return items
      .map((k) => ({
        id: k.id,
        fromUserId: k.fromUserId,
        fromUserName: k.fromUserName,
        toUserId: k.toUserId,
        toUserName: k.toUserName,
        message: k.message,
        createdAt: k.createdAt,
        classification: k.classification,
        classificationConfidence: k.classificationConfidence,
        classificationReasoning: k.classificationReasoning,
        isReviewed: k.isReviewed,
        isHidden: k.isHidden,
      }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  const unreviewedDisplay = useMemo(() => mapKudosForDisplay(unreviewed.data?.kudos || []), [unreviewed.data]);
  const nonKudosDisplay = useMemo(() => mapKudosForDisplay(nonKudos.data?.kudos || []), [nonKudos.data]);
  const unreviewedTotal = unreviewed.data?.total ?? 0;
  const nonKudosTotal = nonKudos.data?.total ?? 0;
  const unreviewedTotalPages = unreviewed.data?.totalPages ?? 0;
  const nonKudosTotalPages = nonKudos.data?.totalPages ?? 0;

  const handleMarkReviewed = async (id: string) => {
    setError(null);
    try {
      await reviewKudos.mutateAsync({ id, data: { approved: true } });
    } catch (e: unknown) {
      const message =
        typeof e === 'object' && e !== null && 'response' in e
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message || '')
          : '';
      setError(message || 'Failed to mark as reviewed');
    }
  };

  const handleHide = async (id: string) => {
    setError(null);
    try {
      await hideKudos.mutateAsync(id);
    } catch (e: unknown) {
      const message =
        typeof e === 'object' && e !== null && 'response' in e
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message || '')
          : '';
      setError(message || 'Failed to hide message');
    }
  };

  const handleChangeClassification = async (id: string, classification: KudosClassification) => {
    setError(null);
    try {
      await reviewKudos.mutateAsync({ id, data: { approved: true, classification } });
    } catch (e: unknown) {
      const message =
        typeof e === 'object' && e !== null && 'response' in e
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message || '')
          : '';
      setError(message || 'Failed to update classification');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) {
      return;
    }

    setError(null);
    try {
      await deleteKudos.mutateAsync(id);
    } catch (e: unknown) {
      const message =
        typeof e === 'object' && e !== null && 'response' in e
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message || '')
          : '';
      setError(message || 'Failed to delete message');
    }
  };

  const renderPagination = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-4 pt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Page {currentPage} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    );
  };

  const renderList = (items: KudosDisplayItem[], emptyText: string, isLoading: boolean, currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (isLoading) {
      return <div className="text-center py-10 text-muted-foreground">Loading...</div>;
    }

    if (items.length === 0) {
      return <div className="text-center py-10 text-muted-foreground">{emptyText}</div>;
    }

    return (
      <div className="space-y-4">
        {items.map((k) => (
          <Card key={k.id}>
            <CardContent className="p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge variant="secondary">{k.classification || 'UNKNOWN'}</Badge>
                    {typeof k.classificationConfidence === 'number' && (
                      <Badge variant="outline">{k.classificationConfidence.toFixed(2)}</Badge>
                    )}
                    {k.isHidden && <Badge variant="destructive">Hidden</Badge>}
                    {k.isReviewed && <Badge variant="outline">Reviewed</Badge>}
                  </div>

                  <KudosCard
                    fromUserName={k.fromUserName}
                    toUserName={k.toUserName}
                    message={k.message}
                    createdAt={k.createdAt}
                    variant="compact"
                    users={users}
                  />

                  {k.classificationReasoning && (
                    <div className="mt-3 text-sm text-muted-foreground">
                      {k.classificationReasoning}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 lg:w-[260px]">
                  <Select
                    value={(k.classification || KudosClassification.OTHER) as string}
                    onValueChange={(val) => handleChangeClassification(k.id, val as KudosClassification)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Set classification" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={KudosClassification.KUDOS}>KUDOS</SelectItem>
                      <SelectItem value={KudosClassification.BIRTHDAY}>BIRTHDAY</SelectItem>
                      <SelectItem value={KudosClassification.WORK_ANNIVERSARY}>WORK_ANNIVERSARY</SelectItem>
                      <SelectItem value={KudosClassification.OTHER}>OTHER</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleMarkReviewed(k.id)}
                      disabled={reviewKudos.isPending || hideKudos.isPending}
                    >
                      Mark reviewed
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleHide(k.id)}
                      disabled={reviewKudos.isPending || hideKudos.isPending}
                    >
                      <EyeOff className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => handleDelete(k.id)}
                      disabled={deleteKudos.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {renderPagination(currentPage, totalPages, onPageChange)}
      </div>
    );
  };

  if (adminLoading || !isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="slack-kudos-review-page">
      <PageHeader title="Slack Kudos Review" description="Review and clean up non-kudos messages from your Slack kudos channel." testId="slack-kudos-review-header" />

      {error && (
        <Alert variant="destructive" data-testid="slack-kudos-review-error-alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card data-testid="slack-kudos-review-card">
        <CardHeader>
          <CardTitle>Review Queue</CardTitle>
          <CardDescription>
            Classify messages correctly, hide non-kudos, and mark items reviewed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="unreviewed" className="space-y-4" data-testid="slack-kudos-review-tabs">
            <TabsList data-testid="slack-kudos-review-tabs-list">
              <TabsTrigger value="unreviewed" data-testid="slack-kudos-review-tab-unreviewed">Unreviewed ({unreviewedTotal})</TabsTrigger>
              <TabsTrigger value="non-kudos" data-testid="slack-kudos-review-tab-non-kudos">Non-kudos ({nonKudosTotal})</TabsTrigger>
            </TabsList>

            <TabsContent value="unreviewed" data-testid="slack-kudos-review-content-unreviewed">
              {renderList(unreviewedDisplay, 'No unreviewed messages.', unreviewed.isLoading, unreviewedPage, unreviewedTotalPages, setUnreviewedPage)}
            </TabsContent>

            <TabsContent value="non-kudos" data-testid="slack-kudos-review-content-non-kudos">
              {renderList(nonKudosDisplay, 'No non-kudos messages.', nonKudos.isLoading, nonKudosPage, nonKudosTotalPages, setNonKudosPage)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SlackKudosReview;
